"use client";

import {
  Alert,
  App,
  Button,
  Flex,
  Form,
  Input,
  Modal,
  Radio,
  Switch,
  Typography,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { type FC, useMemo } from "react";
import { useReactive } from "ahooks";
import { useGlobalState } from "@/state/globalState";
import { logAIData } from "@/actions/logAIData";

const videoTypes = [
  {
    name: "Vertical",
    description: "Vertical video (1080x1920)",
  },
  {
    name: "Horizontal",
    description: "Horizontal video (1920x1080)",
  },
  {
    name: "Sound only",
    description: "Narration audio (mp3)",
  },
  {
    name: "Videofy Project",
    description: "Project file (JSON)",
  },
];

const DEFAULT_RENDER_POLL_TIMEOUT_MS = 1000 * 60 * 15;
const RENDER_POLL_INTERVAL_MS = 3000;

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
}

type FormType = {
  exportType: string;
  logo: boolean;
  audio: boolean;
  voice: boolean;
  music: boolean;
  title: string;
};

type RenderStatusPayload = {
  status?: "running" | "completed" | "failed" | "not-found";
  downloadUrl?: string;
  statusUrl?: string;
  error?: string;
};

const DownloadModal: FC<Props> = ({ open, setOpen }) => {
  const { tabs, processedManuscripts, generationId } = useGlobalState();
  const {
    config: { config },
  } = useGlobalState();
  const { notification } = App.useApp();
  const [downloadForm] = Form.useForm<FormType>();

  const state = useReactive({
    isProcessing: false,
    downloadUrl: undefined as string | undefined,
    error: undefined as string | undefined,
  });

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const pollRenderStatus = async (statusUrl: string) => {
    const startedAt = Date.now();

    while (Date.now() - startedAt < DEFAULT_RENDER_POLL_TIMEOUT_MS) {
      const statusResponse = await fetch(statusUrl, {
        method: "GET",
        cache: "no-store",
      });

      if (statusResponse.status === 404) {
        await sleep(RENDER_POLL_INTERVAL_MS);
        continue;
      }

      let payload: RenderStatusPayload = {};
      try {
        payload = (await statusResponse.json()) as RenderStatusPayload;
      } catch {
        payload = {};
      }

      if (statusResponse.ok && payload.status === "completed" && payload.downloadUrl) {
        return payload.downloadUrl;
      }

      if (!statusResponse.ok || payload.status === "failed") {
        throw new Error(payload.error || "Render failed while polling status");
      }

      await sleep(RENDER_POLL_INTERVAL_MS);
    }

    throw new Error("Render is still running. Please try again in a moment.");
  };

  const playerConfig = useMemo(
    () => ({
      ...(config.player || {}),
      assetBaseUrl:
        typeof window !== "undefined"
          ? window.location.origin
          : process.env.NEXT_PUBLIC_CMS_BASE_URL || "http://127.0.0.1:3000",
    }),
    [config.player]
  );

  const downloadAsJsonProject = (title: string) => {
    const data = {
      config,
      manuscripts: tabs.map(({ manuscript }) => manuscript),
      articleUrls: tabs.map(({ articleUrl }) => articleUrl),
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const currentTime = new Date().toISOString().replace(/[:.]/g, "-");
    a.href = url;
    a.download = `${title || "videofy-project"}-${currentTime}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setOpen(false);
  };

  const downloadAudio = (title: string) => {
    const firstAudioSrc = processedManuscripts[0]?.meta.audio?.src;
    if (!firstAudioSrc) {
      throw new Error("No narration audio found. Process manuscript first.");
    }

    const a = document.createElement("a");
    a.href = firstAudioSrc;
    a.target = "_blank";
    a.rel = "noreferrer";
    a.download = `${title || "narration"}.mp3`;
    a.click();
  };

  const renderLocally = async (values: FormType) => {
    state.error = undefined;
    state.isProcessing = true;
    state.downloadUrl = undefined;

    if (!processedManuscripts || processedManuscripts.length === 0) {
      throw new Error("No manuscripts processed yet. Please process the manuscript before rendering.");
    }

    const orientation =
      values.exportType === "Horizontal" ? "horizontal" : "vertical";

    try {
      const response = await fetch("/api/render/local", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          projectId: generationId,
          orientation,
          manuscripts: processedManuscripts,
          playerConfig,
          voice: values.audio ? values.voice : false,
          backgroundMusic: values.audio ? values.music : false,
          disabledLogo: !values.logo,
        }),
      });

      let payload: RenderStatusPayload = {};
      try {
        payload = (await response.json()) as RenderStatusPayload;
      } catch {
        payload = {};
      }

      if (response.ok && payload.downloadUrl) {
        state.downloadUrl = payload.downloadUrl;
        return;
      }

      if (response.status === 202 && payload.statusUrl) {
        state.downloadUrl = await pollRenderStatus(payload.statusUrl);
        return;
      }

      // Check if API returned structured validation errors
      const errorDetail =
        (payload as any)?.details && (payload as any).details.length > 0
          ? (payload as any).details.map((d: any) => `${d.path}: ${d.message}`).join("; ")
          : payload.error || "Unknown error";

      const bodyText = errorDetail || (await response.text());
      throw new Error(`Render failed (${response.status}): ${bodyText}`);
    } finally {
      state.isProcessing = false;
    }
  };

  const handleDownload = async (values: FormType) => {
    try {
      if (values.exportType === "Videofy Project") {
        downloadAsJsonProject(values.title);
        return;
      }

      if (values.exportType === "Sound only") {
        downloadAudio(values.title);
        return;
      }

      await renderLocally(values);

      if (tabs.length > 0 && generationId) {
        await logAIData(processedManuscripts, generationId);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      state.error = message;
      notification.error({
        title: "Download failed",
        description: message,
        duration: 0,
      });
    }
  };

  if (!config) return <div>Error: No config detected.</div>;

  const defaultExportType =
    config.exportDefaults?.exportType &&
    videoTypes.some((t) => t.name === config.exportDefaults?.exportType)
      ? config.exportDefaults.exportType
      : videoTypes[0]?.name;

  return (
    <Modal open={open} onCancel={() => setOpen(false)} footer={null}>
      <Typography.Title level={2}>Download</Typography.Title>

      <Form<FormType>
        form={downloadForm}
        onFinish={handleDownload}
        layout="vertical"
        initialValues={{
          title: processedManuscripts[0]?.meta.title || "videofy",
          exportType: defaultExportType,
          logo:
            config.exportDefaults?.logo !== undefined
              ? config.exportDefaults.logo
              : true,
          audio:
            config.exportDefaults?.audio !== undefined
              ? config.exportDefaults.audio
              : true,
          voice:
            config.exportDefaults?.voice !== undefined
              ? config.exportDefaults.voice
              : true,
          music:
            config.exportDefaults?.music !== undefined
              ? config.exportDefaults.music
              : true,
        }}
      >
        <Form.Item name="exportType" label="Export type">
          <Radio.Group
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
            options={videoTypes.map((t) => ({
              value: t.name,
              label: t.description,
            }))}
            optionType="button"
            buttonStyle="solid"
          />
        </Form.Item>

        <Form.Item label="Title" name="title">
          <Input className="w-full" />
        </Form.Item>

        <Flex gap="middle">
          <Form.Item label="Logo" name="logo" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="Audio" name="audio" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="Voiceover" name="voice" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="Music" name="music" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Flex>

        <Flex gap="middle">
          <Form.Item shouldUpdate>
            {({ getFieldValue }) => {
              const exportType = getFieldValue("exportType");
              let buttonText = "Process video";
              if (exportType === "Videofy Project") {
                buttonText = "Download";
              } else if (exportType === "Sound only") {
                buttonText = "Download audio";
              } else if (state.isProcessing) {
                buttonText = "Rendering locally...";
              } else if (state.downloadUrl) {
                buttonText = "Re-render";
              }

              return (
                <Button
                  type={state.downloadUrl ? "default" : "primary"}
                  htmlType="submit"
                  size="large"
                  disabled={state.isProcessing}
                  icon={state.isProcessing ? <LoadingOutlined spin /> : undefined}
                >
                  {buttonText}
                </Button>
              );
            }}
          </Form.Item>
          {state.downloadUrl && (
            <Form.Item>
              <Button
                href={state.downloadUrl}
                target="_blank"
                rel="noreferrer"
                type="primary"
                size="large"
              >
                Download video
              </Button>
            </Form.Item>
          )}
        </Flex>

        {state.isProcessing && (
          <Typography.Text type="secondary">
            Render in progress... polling status
          </Typography.Text>
        )}

        {state.error && <Alert type="error" message={`Error: ${state.error}`} />}
      </Form>
    </Modal>
  );
};

export default DownloadModal;
